import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
    MessageBar, MessageBarType, PersonaSize, PersonaPresence, PersonaCoin, Icon, PrimaryButton, Spinner, SpinnerSize
} from 'office-ui-fabric-react';
import { AuditLogQuery, AuditLogItem } from '../../store/services/auditLogService';
import { ApplicationState } from '../../store';
import * as AuditLogsStore from '../../store/AuditLogsStore';
import { PPMFeatures, Subscription } from '../../store/Tenant';
import './AuditLogs.css';
import { CommonOperations, contains } from '../../store/permissions';
import { ControlSpiner } from '../common/Spinner';
import { FieldChanges } from './FieldChanges';
import Collapsible from '../common/Collapsible';
import { groupAuditLogsByTimePeriod, getFirstGroupKey } from './auditLogGrouping';
import { getLabelForEntityType } from './entitySubentityMapping';
import { cultureInfo } from '../utils/common';
import { IBaseEntity } from '../../entities/common';

type StateProps = {
    items: AuditLogItem[];
    isLoading: boolean;
    canLoadMore: boolean;
    error?: string;
    hasBilling: boolean;
    isFeatureEnabled: boolean;
};

type DispatchProps = typeof AuditLogsStore.actionCreators;

type OwnProps = {
    // Only the id is read - not every logged entity carries an attributes bag (a User does not).
    entity: IBaseEntity;
    entityType: string;
    parentEntityId?: string;
    parentEntityType?: string;
};

type Props = StateProps & DispatchProps & OwnProps & RouteComponentProps<{}>;

function EntityActivityLog({ items, isLoading, canLoadMore, error, load, loadMore, clear, entity, entityType, hasBilling, isFeatureEnabled, history, parentEntityId, parentEntityType }: Props) {
    const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});
    const [bottomElement, setBottomElement] = React.useState<HTMLDivElement | null>(null);
    
    // Load audit logs for this specific entity when component mounts or entity changes
    React.useEffect(() => {
        if (entity?.id && isFeatureEnabled) {
            const entityQuery: Partial<AuditLogQuery> = parentEntityType
                ? {
                    subEntityIds: [entity.id],
                    subEntityTypes: [entityType],
                } : {
                    entityIds: [entity.id],
                    entityTypes: [entityType],
                };
            load(entityQuery);
        }
        
        return () => {
            clear();
        };
    }, [entity?.id, entityType, load, clear, isFeatureEnabled, parentEntityId, parentEntityType]);

    // Group items by time periods for better visual organization
    const groupedItems = React.useMemo(() => {
        const grouped = groupAuditLogsByTimePeriod(items);
        
        // Initialize expanded state - expand the first (most recent) group by default
        if (grouped.length > 0 && Object.keys(expandedGroups).length === 0) {
            const firstGroupKey = getFirstGroupKey(grouped);
            if (firstGroupKey) {
                setExpandedGroups({ [firstGroupKey]: true });
            }
        }
        
        return grouped;
    }, [items]);

    React.useEffect(() => {
        if (!bottomElement) {
            return () => {};
        }

        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting && canLoadMore && !isLoading) {
                loadMore();
            }
        }, { root: null, threshold: 0.1 });

        observer.observe(bottomElement);
        return () => observer.disconnect();
    }, [bottomElement, canLoadMore, isLoading, loadMore]);

    if (entity?.id && groupedItems.length === 0 && isLoading && isFeatureEnabled) {
        return (
            <div className="activity-log-container">
                <ControlSpiner isLoading={true} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="activity-log-container">
                <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                    {error}
                </MessageBar>
            </div>
        );
    }

    if (items.length === 0 || !isFeatureEnabled) {
        return (
            <div className="activity-log-container">
                <div className="no-activity-container">
                    <Icon iconName="History" className="no-activity-icon" />
                    <div className="no-activity-content">No activity has been logged yet</div>
                    {hasBilling && !isFeatureEnabled && <PrimaryButton className="upgrade-plan" onClick={() => history.push("/billing")} key="upgrade-plan" text="Upgrade Plan" />}
                </div>
            </div>
        );
    }

    return (
        <div className="activity-log-container">
            <div className="audit-logs-container">
                {groupedItems.map(({ groupKey, items: groupItems }, index) => {
                    const isLastGroup = index === groupedItems.length - 1;
                    
                    return (
                        <div key={groupKey} className="activity-date-group">
                            <Collapsible
                                title={groupKey}
                                expanded={expandedGroups[groupKey] || false}
                                classNames={{
                                    head: "activity-collapsible-header",
                                    body: "activity-collapsible-body"
                                }}
                            >
                                {groupItems.map((item: any) => {
                                    const id = item.id;
                                    const ts = item.timeStamp || '';
                                    const eventType = (item.eventType || '').toLowerCase();
                                    const userName = item.initiatorFullName || '';
                                    const userId = item.initiatorUserId || '';

                                    // Handle attribute deltas
                                    const attributeDeltas = item.changes || {};

                                    return (
                                        <div key={id} className="audit-log-item">
                                            <div className="audit-log-header">
                                                <div className="audit-log-content">
                                                    <div className="user-action-container">
                                                        <div className="user-action-text">
                                                            <PersonaCoin
                                                                size={PersonaSize.size24}
                                                                presence={PersonaPresence.none}
                                                                text={userName || userId}
                                                            />
                                                            <span className="user-name">{userName || userId || "PPM Express"}</span>
                                                            <span>{eventType}</span>
                                                            <span>this {getLabelForEntityType(item.subEntityType || item.entityType)}</span>
                                                        </div>
                                                        
                                                        <div className="timestamp" title={ts}>
                                                            {new Date(ts).toLocaleTimeString(cultureInfo.name, {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>

                                                    {/* Changes */}
                                                    <FieldChanges
                                                        attributeDeltas={attributeDeltas}
                                                        entityType={item.subEntityType || item.entityType}
                                                        description={item.description}
                                                        message={item.message}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* Place infinite scroll sentinel in the last group */}
                                {isLastGroup && (<div ref={(el) => setBottomElement(el)}>
                                    {isLoading && <Spinner size={SpinnerSize.medium} className="spinner-container" label="Loading more..." />}
                                    {!isLoading && !canLoadMore && items.length > 0 && <div className="no-records-message">{`${items.length} audit records shown`}</div>}
                                </div>)}
                            </Collapsible>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    items: state.auditLogs.items,
    isLoading: state.auditLogs.isLoading,
    canLoadMore: state.auditLogs.canLoadMore,
    error: state.auditLogs.error,
    hasBilling: contains(state.user.permissions.common, CommonOperations.BillingManage),
    isFeatureEnabled: Subscription.contains(state.tenant.subscription, PPMFeatures.AuditLog)
});

export default withRouter<OwnProps>(connect(mapStateToProps, AuditLogsStore.actionCreators)(EntityActivityLog));
